import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDocumentUpload } from 'Core/hooks';
import CustomPropTypes from 'CustomPropTypes';
import { Button, ButtonGroup } from 'react-bootstrap';
import Image from 'react-bootstrap/Image';
import { ImageWithBasePath, ZoomableImageModal } from 'Core/components';
import * as Icon from 'react-feather';
import { Download } from 'react-feather';
import { downloadImage } from 'Core/utils';
import { QUALITY_STATUS_ID } from 'Core/constants/constants';
import { format } from 'date-fns';

const styles = {
  uploadContainer: {
    background: 'white',
    borderRadius: '4px',
    padding: '15px',
  },
  uploadArea: {
    marginBottom: '15px',
  },
  uploadLabel: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: '120px',
    border: '2px dashed #ccc',
    borderRadius: '4px',
    cursor: 'pointer',
    backgroundColor: '#f9f9f9',
    transition: 'background-color 0.2s',
  },
  uploadContent: {
    textAlign: 'center',
  },
  uploadIcon: {
    fontSize: '16px',
    color: '#666',
    marginBottom: '8px',
  },
  uploadText: {
    fontSize: '14px',
    color: '#666',
    marginBottom: '4px',
  },
  uploadBold: {
    fontWeight: 600,
  },
  uploadSubtext: {
    fontSize: '12px',
    color: '#888',
  },
  hiddenInput: {
    display: 'none',
  },
  previewGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
    gap: '15px',
    marginTop: '15px',
  },
  previewItem: {
    position: 'relative',
    borderRadius: '4px',
    overflow: 'hidden',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
  },
  previewImageContainer: {
    position: 'relative',
    paddingTop: '75%' /* 4:3 Aspect Ratio */,
  },
  previewImage: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  removeButton: {
    position: 'absolute',
    top: '8px',
    right: '8px',
    width: '20px',
    height: '20px',
    borderRadius: '50%',
    backgroundColor: '#ff4444',
    color: 'white',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '14px',
    opacity: 0,
    transition: 'opacity 0.2s',
  },
  fileName: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    padding: '6px 8px',
    fontSize: '12px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
};

const initData = {
  id: 0,
  qualityApprovalId: 0,
  fileName: '',
  filePath: '',
  relativePath: '',
};

const formatDateTime = value => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return format(date, 'dd-MMM-yyyy HH:mm');
};

const QualityCategory = ({ data, onAction, onRequestEvidence, onCommentUpdate, key }) => {
  const [toolFilesInputs, setToolFileInputs] = useState(initData);
  const [toolFiles, setToolFiles] = useState(data);
  // const [data, setData] = useState(newdata);
  // useEffect(() => {
  //   setData(newdata)
  // }, [newdata])
  const updateToolFileField = (field, value) => {
    setToolFileInputs(prevToolFileInputs => ({
      ...prevToolFileInputs,
      [field]: value,
    }));
  };

  const { uploadDocuments, loading } = useDocumentUpload();
  const [isHovering, setIsHovering] = useState(false);
  
  // State for zoomable image modal
  const [showZoomModal, setShowZoomModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  const handleFileUpload = async event => {
    const files = Array.from(event.target.files);

    const formData = new FormData();
    formData.append('file', files[0]);
    formData.append('UploadCategory', 'Inspection-QualityCategory');

    const result = await uploadDocuments(formData);
    if (result) {
      const newFile = {
        id: Math.floor(Math.random() * 101),
        fileName: result.fileName,
        filePath: result.filePath,
        relativePath: result.relativePath,
      };
      const newFiles = [...toolFiles, newFile];

      setToolFiles(newFiles);
      setToolFileInputs(initData);
      // if (onAction) onAction(newFiles);
    }
  };

  const downloadImageFn = (e, img) => {
    e.stopPropagation();
    downloadImage(img);
  };

  // Handler for opening zoom modal
  const handleImageClick = (e, image) => {
    e.stopPropagation();
    setSelectedImage(image);
    setShowZoomModal(true);
  };

  // Handler for closing zoom modal
  const handleCloseZoomModal = () => {
    setShowZoomModal(false);
    setSelectedImage(null);
  };

  // Handler for download from zoom modal
  const handleZoomModalDownload = (imageSrc) => {
    downloadImage(imageSrc);
  };

  const removeFile = fileId => {
    const newFiles = toolFiles.filter(toolFile => toolFiles.id !== fileId);
    setToolFiles(newFiles);
    // if (onAction) onAction(newFiles);
  };

  return (
    <>
      <div className="row" key={key}>
        <div className="d-flex-centered-v col-7">
          <div className="d-flex-centered-v w-100 col align-items-start partDataContainer">
            <div className="fs-6 row d-flex w-100 pb-1">
              <label className="fs-6 col-sm-12 col-md-6 d-flex">
                <div className="row w-100 d-flex flex-row">
                  <label className="fw-bold col-sm-12 col-md-6">Requested By:</label>{' '}
                  <label className="col-sm-12 col-md-6">{data.createdBy || '-'}</label>
                </div>
              </label>
              <label className="fs-6 col-sm-12 col-md-6 d-flex">
                <div className="row w-100 d-flex flex-row">
                  <label className="fw-bold col-sm-12 col-md-6">Requested Date:</label>{' '}
                  <label className="col-sm-12 col-md-6">{formatDateTime(data.createdDate)}</label>
                </div>
              </label>
            </div>
            <div className="fs-6 row d-flex w-100 pb-1">
              <label className="fs-6 col-sm-12 col-md-6 d-flex">
                <div className="row w-100 d-flex flex-row">
                  <label className="fw-bold col-sm-12 col-md-6">Work Ticket:</label>{' '}
                  <label className="col-sm-12 col-md-6">{data.workTicket || '-'}</label>
                </div>
              </label>
            </div>
            <label className="pb-1 fs-6 row d-flex w-100">
              <label className="fw-bold col-sm-12 col-md-3">Question:</label>{' '}
              <label className="col-sm-12 col-md-9 ps-0">{data.request}</label>
            </label>
            <label className="pb-1 fs-6 row d-flex w-100">
              <label className="fw-bold col-sm-12 col-md-3">Comments:</label>{' '}
              {/* <label className="col-sm-12 col-md-9">{data.comments}</label> */}
              <textarea
                className={`textAreaComment ms-2 mt-2 ${data.isError ? 'border-danger' : ''}`}
                onChange={e => onCommentUpdate(data.requestId, e.target.value)}
                rows="4"
                cols="50"
                maxLength="250"
                value={data.comments || ''}
              />
              {data.isError && <label className="text-danger">Comment is mandatory</label>}
            </label>
          </div>
          <div className="pt-3 d-flex-centered-h buttonContainer col flex-column flex-md-row">
            <Button
              variant="success"
              className="actionButton"
              disabled={!data.images || data.images.length === 0}
              onClick={() => onAction(QUALITY_STATUS_ID.APPROVED, data)}
            >
              Approve
            </Button>
            <Button
              className="ms-md-3 actionButton"
              variant="danger"
              disabled={!data.images || data.images.length === 0}
              onClick={() => onAction(QUALITY_STATUS_ID.REJECTED, data)}
            >
              Reject
            </Button>
            <Button
              className="ms-md-3 actionButton"
              variant="info"
              disabled={!data.images || data.images.length === 0}
              onClick={() => onAction(QUALITY_STATUS_ID.CONDITIONALLY_APPROVED, data)}
            >
              Conditional Approve
            </Button>
            <Button
              className="ms-md-3 actionButton"
              variant="warning"
              onClick={() => onRequestEvidence(data)}
            >
              Request Evidence
            </Button>
          </div>
        </div>
        <div className="d-flex-centered-h col-5 flex-wrap">
          {(data.images || []).map((image, index) => (
            <div className="position-relative" key={index}>
              <Image
                src={image.relativePath}
                width="200px"
                height="150px"
                className="qualityapproval-image"
                thumbnail
                style={{ cursor: 'pointer' }}
                onClick={e => handleImageClick(e, image)}
              />
              <ButtonGroup className="downloadImageBtn" size="sm">
                <Button variant="light" onClick={e => downloadImageFn(e, image.relativePath)}>
                  <Download size={16} />
                </Button>
                ,
              </ButtonGroup>
            </div>
          ))}
        </div>
      </div>
      
      {/* Zoomable Image Modal */}
      <ZoomableImageModal
        show={showZoomModal}
        onHide={handleCloseZoomModal}
        imageSrc={selectedImage?.relativePath}
        imageAlt={selectedImage?.fileName || 'Quality Approval Image'}
        onDownload={handleZoomModalDownload}
      />
    </>
  );
};

QualityCategory.defaultProps = {
  onAction: () => {},
  onRequestEvidence: () => {},
  data: [],
};

QualityCategory.propTypes = {
  onAction: PropTypes.func,
  onRequestEvidence: PropTypes.func,
  data: PropTypes.shape(CustomPropTypes.QualityApproval),
};

export default QualityCategory;
